import { AlertModal } from '@modusign/design-system';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useMount } from 'react-use';

import { useUrlQuery } from '@hooks';

import RootLoadingSpinnerComponent from '@app/components/Loading/RootLoadingSpinnerComponent';
import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import {
  SETUP_DOCUMENT_BY_DRAFT_ERROR_LIST,
  useSetupDocumentByEmbeddedDraft,
  useSetupPreviewByEmbeddedDraft,
  useSetupTemplateByEmbeddedDraft,
} from '@app/features/integration/embeddedDraft';
import embeddedService, { EmbeddedServiceError } from '@app/libs/embedded/embeddedService';
import redirect from '@app/libs/embedded/redirect';
import { logger } from '@app/libs/logger';
import { loadAccount } from '@app/store/account/actions';
import { clearDocumentSetupState } from '@app/store/documentSetup/actions';

function validateMode(mode: string): mode is 'request' | 'preview' | 'create-template' {
  return ['request', 'preview', 'create-template'].includes(mode);
}

const INVALID_MODE = 'INVALID_MODE';

export default function EmbeddedDraft() {
  const query = useUrlQuery();
  const mode = query.get('mode') ?? 'request';
  const dispatch = useDispatch();
  const { draftId } = useParams<{ draftId: string }>();
  const history = useHistory();

  const { load: loadContractSetup } = useSetupDocumentByEmbeddedDraft();
  const { load: loadPreview } = useSetupPreviewByEmbeddedDraft();
  const { load: loadTemplate } = useSetupTemplateByEmbeddedDraft();

  const [error, setError] = useState<{ title: string; content: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const initializeEditor = async (id: string) => {
    try {
      setIsLoading(true);
      if (!validateMode(mode)) {
        throw new Error(INVALID_MODE);
      }

      dispatch(loadAccount());
      dispatch(clearDocumentSetupState());

      if (mode === 'preview') {
        await loadPreview({ draftId: id });
        history.push('/draft/preview');
      } else if (mode === 'request') {
        await loadContractSetup({ draftId: id });
        history.push('/editor/others-require/set');
      } else if (mode === 'create-template') {
        await loadTemplate({ draftId: id });
        history.push('/template/setup/participant-setup');
      }
    } catch (error) {
      if ([INVALID_MODE].includes(error.message)) {
        setError({
          title: '유효하지 않은 접근',
          content: '유효하지 않은 접근입니다.',
        });
      } else if (SETUP_DOCUMENT_BY_DRAFT_ERROR_LIST.includes(error.message)) {
        setError({
          title: '서명 요청 안내',
          content:
            '서명자의 사인/도장 입력란이 설정되지 않아 서명을 요청할 수 없어요.\nPC와 같이 넓은 화면에서 이용해 주세요.',
        });
      } else {
        setError({
          title: '초안 불러오기 실패',
          content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useMount(() => {
    if (!embeddedService.isEmbedded) {
      return;
    }

    if (!validateMode(mode)) {
      logger.error(`embedded mode is not valid in embedded draft page.`, {
        draftId,
        mode,
        error: embeddedService.error,
      });
      setError({
        title: '유효하지 않은 접근',
        content: '유효하지 않은 접근입니다.',
      });
      return;
    }
    switch (embeddedService.error) {
      case null:
        initializeEditor(draftId);
        break;
      case EmbeddedServiceError.EXPIRED_ACCESS_TOKEN:
        setError({
          title: '만료된 URL',
          content: 'URL이 만료되었습니다.\n다시 발급해 주세요.',
        });
        break;
      default:
        logger.error(`embedded service error occurred in embedded draft page.`, {
          draftId,
          error: embeddedService.error,
        });
        setError({
          title: '유효하지 않은 접근',
          content: '유효하지 않은 접근입니다.',
        });
    }
  });

  return (
    <div className="align-center">
      {error && (
        <AlertModal
          isOpen
          maxWidth="xxs"
          title={error.title}
          variant="warning"
          onClose={() => redirect('https://www.modusign.co.kr/')}
        >
          {error.content}
        </AlertModal>
      )}
      {isLoading && <RootLoadingSpinnerComponent isLoading isOverlay />}
    </div>
  );
}
